import { useState } from 'react'
import { Plus, Search, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useMarcas, useCreateMarca, useUpdateMarca, useDeleteMarca, useLaboratorios,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { MarcaFormDialog } from './MarcaFormDialog'
import type { Marca } from '@/modules/multichannel/types/configuracion'

export function MarcasTab() {
  const [search, setSearch] = useState('')
  const [labFilter, setLabFilter] = useState<number | undefined>()
  const [showInactive, setShowInactive] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Marca | null>(null)

  const { data: laboratorios } = useLaboratorios({ activo: true })
  const { data: marcas, isLoading } = useMarcas({
    marca_principal_id: labFilter,
    ...(showInactive ? {} : { activo: true }),
  })
  const createMutation = useCreateMarca()
  const updateMutation = useUpdateMarca()
  const deleteMutation = useDeleteMarca()

  const filtered = (marcas || []).filter(m =>
    !search || m.nombre.toLowerCase().includes(search.toLowerCase())
  )

  const handleCreate = () => { setEditing(null); setDialogOpen(true) }
  const handleEdit = (marca: Marca) => { setEditing(marca); setDialogOpen(true) }
  const handleDelete = async (marca: Marca) => {
    if (window.confirm(`Eliminar marca "${marca.nombre}"?`)) {
      await deleteMutation.mutateAsync(marca.id)
    }
  }
  const handleToggleActivo = async (marca: Marca) => {
    await updateMutation.mutateAsync({ id: marca.id, data: { activo: !marca.activo } })
  }

  return (
    <div className="space-y-4">
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Marcas
              {marcas && <Badge className="badge-gradient text-xs">{filtered.length}</Badge>}
            </CardTitle>
            <Button onClick={handleCreate} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" /> Nueva
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <select
              value={labFilter || ''}
              onChange={e => setLabFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="flex h-9 w-full md:w-56 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">Todos los laboratorios</option>
              {(laboratorios || []).map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por nombre..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button variant={showInactive ? 'default' : 'outline'} size="sm" onClick={() => setShowInactive(!showInactive)}
              className={showInactive ? 'bg-teal-800 hover:bg-teal-900 text-white' : ''}>
              {showInactive ? 'Mostrar solo activos' : 'Mostrar todos'}
            </Button>
          </div>

          {isLoading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filtered.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Laboratorio</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Descripcion</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(marca => (
                    <TableRow key={marca.id}>
                      <TableCell>{marca.marca_principal_nombre}</TableCell>
                      <TableCell className="font-medium">{marca.nombre}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{marca.descripcion || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={marca.activo} onCheckedChange={() => handleToggleActivo(marca)}
                            className={marca.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                          <Badge className={marca.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {marca.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(marca)}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(marca)}><Trash2 className="h-4 w-4 text-gray-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {search || labFilter ? 'No se encontraron marcas.' : 'No hay marcas creadas.'}
            </div>
          )}
        </CardContent>
      </Card>

      <MarcaFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null) }}
        marca={editing}
        laboratorios={laboratorios || []}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
